/**
 * A run's "Open task", "Open plan" and roadmap item, and its activity, in the console: the
 * workbench's `timelineOpen.ts` (which opens the run's repository on the desktop and navigates its
 * views) and `history/HistoryLink.tsx` (the run as a graph) swapped for console navigation. Every
 * repository of the project is here, so there is no "not on this computer": a task, a plan or a
 * roadmap item opens the project's Work tab on that part, and "Show its activity" picks the run
 * whose activity graph History shows beneath the timeline.
 *
 * The page that draws a timeline says where those go (`TimelineOpenProvider`); without one, the
 * actions are not offered.
 */
import type { RoadmapRef, TimelineCommit } from '@aioe/shared';
import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { projectTabParams } from '../projectTabs.tsx';
import type { TimelineRun } from './workTimelineModel.ts';

export interface OpenResult {
  ok: boolean;
  message?: string;
}

interface TimelineOpenTarget {
  projectId: string;
  /** The run's activity graph: History's `run` (projectHistory.tsx). */
  onRun?: (runId: string) => void;
  /** The run whose activity shows now. */
  shownRun?: string;
}

const TimelineOpenContext = createContext<TimelineOpenTarget | undefined>(undefined);

export function TimelineOpenProvider({ value, children }: { value: TimelineOpenTarget; children: ReactNode }) {
  return <TimelineOpenContext.Provider value={value}>{children}</TimelineOpenContext.Provider>;
}

export interface TimelineOpeners {
  openRunTask(run: TimelineRun): Promise<OpenResult>;
  openRunPlan(run: TimelineRun): Promise<OpenResult>;
  openRoadmapItem(run: TimelineRun, item: Pick<RoadmapRef, 'rootId' | 'id'>): Promise<OpenResult>;
  /** A commit marker's "Open on GitHub": its page there, in a new tab. */
  openTimelineCommit(commit: Pick<TimelineCommit, 'hash'>, url: string | undefined): Promise<OpenResult>;
  /** A commit's task: the project's Work tab, on its tasks. */
  openCommitTask(commit: Pick<TimelineCommit, 'task'>): Promise<OpenResult>;
  /** Show the run's activity graph; undefined where the page has none. */
  showActivity?: (run: TimelineRun) => void;
  shownRun?: string;
  /** Nothing to open into: the page gave no project. */
  none: boolean;
}

export function useTimelineOpen(): TimelineOpeners {
  const target = useContext(TimelineOpenContext);
  const navigate = useNavigate();
  return useMemo(() => {
    const go = (part: 'tasks' | 'plans' | 'map'): Promise<OpenResult> => {
      if (!target) return Promise.resolve({ ok: false, message: 'There is nowhere to open it from here.' });
      navigate(`/projects/${encodeURIComponent(target.projectId)}?${new URLSearchParams(projectTabParams('work', part))}`);
      return Promise.resolve({ ok: true });
    };
    return {
      openRunTask: (run) => (run.task ? go('tasks') : Promise.resolve({ ok: false, message: 'This run was not on a task.' })),
      openRunPlan: (run) => (run.plan ? go('plans') : Promise.resolve({ ok: false, message: 'This run was not part of a plan.' })),
      openRoadmapItem: () => go('map'),
      openTimelineCommit: (_commit, url) => {
        if (!url) return Promise.resolve({ ok: false, message: 'This commit has no page on GitHub to open.' });
        window.open(url, '_blank', 'noopener,noreferrer');
        return Promise.resolve({ ok: true });
      },
      openCommitTask: (commit) => (commit.task ? go('tasks') : Promise.resolve({ ok: false, message: 'This commit names no task.' })),
      ...(target?.onRun ? { showActivity: (run: TimelineRun) => target.onRun?.(run.id) } : {}),
      ...(target?.shownRun ? { shownRun: target.shownRun } : {}),
      none: !target,
    };
  }, [target, navigate]);
}
